/**
 * Compatibility Layer: Redux Store with Context API Interface
 * This file provides backward compatibility with the old Store context API
 * while using Redux under the hood for global state management
 */

import React, { createContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCategories,
  addCategory as addCategoryAction,
  updateCategory as updateCategoryAction,
  deleteCategory as deleteCategoryAction,
  addCategoryAsync,
  updateCategoryAsync,
  deleteCategoryAsync,
} from '../store/slices/categoriesSlice';
import {
  setSubcategories,
  addSubcategory as addSubcategoryAction,
  updateSubcategory as updateSubcategoryAction,
  deleteSubcategory as deleteSubcategoryAction,
  addSubcategoryAsync,
  updateSubcategoryAsync,
  deleteSubcategoryAsync,
} from '../store/slices/subcategoriesSlice';
import {
  updateContactInfo as updateContactInfoAction,
  updateContactInfoAsync,
} from '../store/slices/contactInfoSlice';
import {
  lawyerLogin,
  lawyerLogout,
  adminLogin,
  adminLogout,
  clearUserInfo,
  resetUserInfo,
} from '../store/slices/authSlice';

// Create context for backward compatibility
export const Store = createContext();

// Compatibility Provider Component
export function StoreProvider({ children }) {
  const dispatch = useDispatch();
  
  // Get state from Redux
  const categories = useSelector((state) => state.categories.items);
  const subcategories = useSelector((state) => state.subcategories.items);
  const contactInfo = useSelector((state) => state.contactInfo.data);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const admin = useSelector((state) => state.auth.admin);
  const firestoreInitialized = useSelector((state) => state.categories.loading === false);
  const useFirestore = false; // Not using Firestore anymore, using API service

  // Create compatibility state object
  const state = useMemo(
    () => ({
      Categories: categories || [],
      Subcategories: subcategories || [],
      ContactInfo: contactInfo || { contact_phone: [] },
      UserInfo: userInfo,
      Admin: admin,
    }),
    [categories, subcategories, contactInfo, userInfo, admin]
  );

  // Create compatibility dispatch function
  const compatibilityDispatch = useMemo(
    () => (action) => {
      switch (action.type) {
        // Auth actions
        case 'LawyerLogin':
          dispatch(lawyerLogin(action.payload));
          break;
        case 'LawyerLogout':
          dispatch(lawyerLogout());
          break;
        case 'AdminLogin':
          dispatch(adminLogin(action.payload));
          break;
        case 'AdminLogout':
          dispatch(adminLogout());
          break;
        case 'ClearUserInfo':
          dispatch(clearUserInfo());
          break;
        case 'ResetUserInfo':
          dispatch(resetUserInfo());
          break;
        case 'UserLoggedIn':
          dispatch(lawyerLogin(action.payload));
          break;
        case 'update':
          // Legacy action, map to lawyerLogin
          dispatch(lawyerLogin(action.payload));
          break;

        // Contact Info actions
        case 'UpdateContactInfo':
          // Use async thunk for API sync
          dispatch(updateContactInfoAsync(action.payload));
          // Also update immediately for UI responsiveness
          dispatch(updateContactInfoAction(action.payload));
          break;

        // Category actions
        case 'SetCategories':
          dispatch(setCategories(action.payload));
          // Also sync to localStorage for compatibility
          if (typeof window !== 'undefined') {
            localStorage.setItem('Categories', JSON.stringify(action.payload));
          }
          break;
        case 'AddCategory':
          // Add immediately for UI responsiveness
          dispatch(addCategoryAction(action.payload));
          // Also sync to localStorage immediately
          if (typeof window !== 'undefined') {
            const current = JSON.parse(localStorage.getItem('Categories') || '[]');
            current.push(action.payload);
            localStorage.setItem('Categories', JSON.stringify(current));
          }
          // Then sync to API in background
          dispatch(addCategoryAsync(action.payload));
          break;
        case 'UpdateCategory':
          // Update immediately for UI responsiveness
          dispatch(updateCategoryAction(action.payload));
          // Also sync to localStorage immediately
          if (typeof window !== 'undefined') {
            const current = JSON.parse(localStorage.getItem('Categories') || '[]');
            const index = current.findIndex(cat => String(cat.id) === String(action.payload.id));
            if (index !== -1) {
              current[index] = action.payload;
              localStorage.setItem('Categories', JSON.stringify(current));
            }
          }
          // Then sync to API in background
          dispatch(updateCategoryAsync({ id: action.payload.id, category: action.payload }));
          break;
        case 'DeleteCategory':
          // Delete immediately for UI responsiveness
          dispatch(deleteCategoryAction(action.payload));
          // Also sync to localStorage immediately
          if (typeof window !== 'undefined') {
            const current = JSON.parse(localStorage.getItem('Categories') || '[]');
            const filtered = current.filter(cat => String(cat.id) !== String(action.payload));
            localStorage.setItem('Categories', JSON.stringify(filtered));
            // Also delete related subcategories
            const subs = JSON.parse(localStorage.getItem('Subcategories') || '[]');
            const filteredSubs = subs.filter(sub => String(sub.category_id) !== String(action.payload));
            localStorage.setItem('Subcategories', JSON.stringify(filteredSubs));
          }
          // Then sync to API in background
          dispatch(deleteCategoryAsync(action.payload));
          break;

        // Subcategory actions
        case 'SetSubcategories':
          dispatch(setSubcategories(action.payload));
          // Also sync to localStorage for compatibility
          if (typeof window !== 'undefined') {
            localStorage.setItem('Subcategories', JSON.stringify(action.payload));
          }
          break;
        case 'AddSubcategory':
          // Add immediately for UI responsiveness
          dispatch(addSubcategoryAction(action.payload));
          // Also sync to localStorage immediately
          if (typeof window !== 'undefined') {
            const current = JSON.parse(localStorage.getItem('Subcategories') || '[]');
            current.push(action.payload);
            localStorage.setItem('Subcategories', JSON.stringify(current));
          }
          // Then sync to API in background
          dispatch(addSubcategoryAsync(action.payload));
          break;
        case 'UpdateSubcategory':
          // Update immediately for UI responsiveness
          dispatch(updateSubcategoryAction(action.payload));
          // Also sync to localStorage immediately
          if (typeof window !== 'undefined') {
            const current = JSON.parse(localStorage.getItem('Subcategories') || '[]');
            const index = current.findIndex(sub => String(sub.id) === String(action.payload.id));
            if (index !== -1) {
              current[index] = action.payload;
              localStorage.setItem('Subcategories', JSON.stringify(current));
            }
          }
          // Then sync to API in background
          dispatch(updateSubcategoryAsync({ id: action.payload.id, subcategory: action.payload }));
          break;
        case 'DeleteSubcategory':
          // Delete immediately for UI responsiveness
          dispatch(deleteSubcategoryAction(action.payload));
          // Also sync to localStorage immediately
          if (typeof window !== 'undefined') {
            const current = JSON.parse(localStorage.getItem('Subcategories') || '[]');
            const filtered = current.filter(sub => String(sub.id) !== String(action.payload));
            localStorage.setItem('Subcategories', JSON.stringify(filtered));
          }
          // Then sync to API in background
          dispatch(deleteSubcategoryAsync(action.payload));
          break;

        default:
          console.warn(`Unknown action type: ${action.type}`);
      }
    },
    [dispatch]
  );

  const value = useMemo(
    () => ({
      state,
      dispatch: compatibilityDispatch,
      firestoreInitialized,
      useFirestore,
    }),
    [state, compatibilityDispatch, firestoreInitialized, useFirestore]
  );

  return <Store.Provider value={value}>{children}</Store.Provider>;
}
